/**
 * Appointment Scheduling Service
 * Handles resource availability and facility appointment APIs
 */

import { apiClient } from '@/services/core/apiClient';
import { graphqlClient } from '@/services/core/graphqlClient';
import { GET_ALL_RESOURCES, GET_CLINIC_FACILITIES } from '@/services/resources/appointmentSchedulingGquery';

// Types
export interface Resource {
  id: number;
  email: string;
  user_name: string;
  user_type: string;
}

export interface Facility {
  clinic_id: number;
  facility_name: string;
  clinic_facility_id: number;
}

// Resource availability types for consolidated endpoint
export interface ResourceAvailabilitySlot {
  userId: number;
  availabilityDayTimeId: number;
  resourceTimezone: string;
  originalStartTime: string;
  originalEndTime: string;
  originalDisplayTime: string;
  convertedStartDateTime: string;
  convertedEndDateTime: string;
  convertedStartTime: string;
  convertedEndTime: string;
  convertedDisplayTime: string;
  bookedCount: number;
  timezoneOffset: string;
}

export interface ResourceInfo {
  userId: number;
  resourceTimezone: string;
  availableDaysCount: number;
}

export interface ResourceAvailabilityResponse {
  success: boolean;
  data: {
    userIds: number[];
    year: number;
    month: number;
    patientTimezone: string;
    availableDates: string[];
    availabilityByDate: Record<string, ResourceAvailabilitySlot[]>;
    resources: ResourceInfo[];
  };
  timestamp: number;
}

// Resource time slots types for consolidated date endpoint
export interface ResourceTimeSlot {
  availabilityDayTimeId: number;
  originalStartTime: string;
  originalEndTime: string;
  originalDisplayTime: string;
  convertedStartDateTime: string;
  convertedEndDateTime: string;
  convertedStartTime: string;
  convertedEndTime: string;
  convertedDisplayTime: string;
  bookedCount: number;
  timezoneOffset: string;
}

export interface ResourceTimeSlotGroup {
  userId: number;
  resourceTimezone: string;
  timeSlots: ResourceTimeSlot[];
}

export interface BookedAppointment {
  appointmentId: number;
  userId?: number;
  resourceAvailabilityDayTimeId?: number;
  facilityAvailabilityDayTimeId?: number;
  startDateTime: string;
  endDateTime: string;
  displayTime: string;
  appointmentReason: string;
}

export interface ResourceTimeSlotsResponse {
  success: boolean;
  data: {
    userIds: number[];
    date: string;
    dayOfWeek: number;
    dayOfWeekName: string;
    patientTimezone: string;
    resourceTimeSlots: ResourceTimeSlotGroup[];
    bookedAppointments: BookedAppointment[];
  };
  timestamp: number;
}

// Facility availability types (unchanged)
export interface FacilityAvailabilityResponse {
  success: boolean;
  data: {
    clinicFacilityId: number;
    year: number;
    month: number;
    availableDates: string[];
  };
  timestamp: number;
}

export interface TimeSlot {
  availabilityDayTimeId: number;
  startTime: string;
  endTime: string;
  displayTime: string;
  bookedCount: number;
}

export interface FacilityTimeSlotsResponse {
  success: boolean;
  data: {
    clinicFacilityId: number;
    date: string;
    dayOfWeek: number;
    dayOfWeekName: string;
    timeSlots: TimeSlot[];
    bookedAppointments: BookedAppointment[];
  };
  timestamp: number;
}

class AppointmentSchedulingService {
  /**
   * Fetch all resources (users) for virtual appointments
   */
  async getResources(hasuraToken: string): Promise<Resource[]> {
    try {
      const response = await graphqlClient.query<{ pep_vw_user_dashboard: Resource[] }>(
        GET_ALL_RESOURCES,
        {},
        hasuraToken
      );
      return response.data?.pep_vw_user_dashboard || [];
    } catch (error) {
      console.error('Error fetching resources:', error);
      throw error;
    }
  }

  /**
   * Fetch clinic facilities for in-person appointments
   */
  async getClinicFacilities(hasuraToken: string, clinicId: number): Promise<Facility[]> {
    try {
      const response = await graphqlClient.query<{ emr_clinic_facility: Facility[] }>(
        GET_CLINIC_FACILITIES,
        { clinicId },
        hasuraToken
      );
      return response.data?.emr_clinic_facility || [];
    } catch (error) {
      console.error('Error fetching clinic facilities:', error);
      throw error;
    }
  }

  /**
   * Fetch consolidated resource availability dates for a month
   * GET /api/ngpep/{userId}/consolidated/{year}/{month}
   */
  async getResourceAvailability(
    userId: number,
    year: number,
    month: number,
    token: string,
    userIds: number[],
    patientTimezone?: string
  ): Promise<ResourceAvailabilityResponse> {
    try {
      const userIdsParam = userIds.join(',');
      
      let url = `/api/ngpep/${userId}/consolidated/${year}/${month}?userIds=${userIdsParam}`;
      if (patientTimezone) {
        url += `&patientTimezone=${encodeURIComponent(patientTimezone)}`;
      }
      
      const response = await apiClient.get<ResourceAvailabilityResponse>(
        url,
        token
      );
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.error || 'Failed to fetch resource availability');
    } catch (error) {
      console.error('Error fetching resource availability:', error);
      throw error;
    }
  }

  /**
   * Fetch consolidated resource time slots for a specific date
   * GET /api/ngpep/{userId}/consolidated/date/{date}
   */
  async getResourceTimeSlots(
    userId: number,
    date: string,
    token: string,
    userIds: number[],
    patientTimezone?: string
  ): Promise<ResourceTimeSlotsResponse> {
    try {
      const userIdsParam = userIds.join(',');
      
      let url = `/api/ngpep/${userId}/consolidated/date/${date}?userIds=${userIdsParam}`;
      if (patientTimezone) {
        url += `&patientTimezone=${encodeURIComponent(patientTimezone)}`;
      }
      
      const response = await apiClient.get<ResourceTimeSlotsResponse>(
        url,
        token
      );
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.error || 'Failed to fetch resource time slots');
    } catch (error) {
      console.error('Error fetching resource time slots:', error);
      throw error;
    }
  }

  /**
   * Fetch facility availability dates for a month
   */
  async getFacilityAvailability(
    userId: number,
    clinicFacilityId: number,
    year: number,
    month: number,
    token: string
  ): Promise<FacilityAvailabilityResponse> {
    try {
      const response = await apiClient.get<FacilityAvailabilityResponse>(
        `/api/ngpep/${userId}/appointment/dates/${clinicFacilityId}/${year}/${month}`,
        token
      );
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.error || 'Failed to fetch facility availability');
    } catch (error) {
      console.error('Error fetching facility availability:', error);
      throw error;
    }
  }

  /**
   * Fetch facility time slots for a specific date
   */
  async getFacilityTimeSlots(
    userId: number,
    clinicFacilityId: number,
    date: string,
    token: string
  ): Promise<FacilityTimeSlotsResponse> {
    try {
      const response = await apiClient.get<FacilityTimeSlotsResponse>(
        `/api/ngpep/${userId}/appointment/time-slots/${clinicFacilityId}/${date}`,
        token
      );
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.error || 'Failed to fetch facility time slots');
    } catch (error) {
      console.error('Error fetching facility time slots:', error);
      throw error;
    }
  }
}

export const appointmentSchedulingService = new AppointmentSchedulingService();
